import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { ModuleAccessProvider, ModuleGuard } from './hooks/useModuleAccess';
import AssistantWidget from './components/assistant/AssistantWidget';
import { AuthProvider } from './context/AuthContext';
import { PermissionProvider, usePermissions } from './context/PermissionContext';
import { AlertProvider } from './context/AlertContext';
import { OrgProvider } from './context/OrgContext';
import { SiteProvider } from './context/SiteContext';
import { Toaster } from './components/ui/toaster';
import { Toaster as SonnerToaster } from './components/ui/sonner';
import useIdleTimeout from './hooks/useIdleTimeout';
import { clearAllListFilters } from './hooks/useListFilters';
import IdleTimeoutWarning from './components/IdleTimeoutWarning';
import Footer from './components/Footer';
import Login from './pages/Login';
import CompanyRegister from './pages/CompanyRegister';
import ChangePassword from './pages/ChangePassword';
import AcceptAgreement from './pages/AcceptAgreement';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import ReportHazard from './pages/ReportHazard';
import HazardsList from './pages/HazardsList';
import HazardDetail from './pages/HazardDetail';
import IncidentsList from './pages/IncidentsList';
import IncidentDetail from './pages/IncidentDetail';
import ObservationsList from './pages/observations/ObservationsList';
import ObservationDetail from './pages/observations/ObservationDetail';
import ReportObservation from './pages/observations/ReportObservation';
import UserManagement from './pages/UserManagement';
import AdminManagement from './pages/AdminManagement';
// Phase 1 Pages
import ExecutiveDashboard from './pages/dashboards/ExecutiveDashboard';
import SupervisorDashboard from './pages/dashboards/SupervisorDashboard';
import { canAccessSupervisorDashboard, canAccessWorkforce } from './dashboard/presets/safety';
import CAPAList from './pages/capa/CAPAList';
import CAPADetail from './pages/capa/CAPADetail';
import CAPACreate from './pages/capa/CAPACreate';
// Phase 2 - HIRA Module
import HIRAList from './pages/hira/HIRAList';
import HIRACreate from './pages/hira/HIRACreate';
import HIRADetail from './pages/hira/HIRADetail';
// Phase 2 - Training Module
import TrainingDashboard from './pages/training/TrainingDashboard';
import TrainingCourses from './pages/training/TrainingCourses';
import TrainingRecords from './pages/training/TrainingRecords';
import Certifications from './pages/training/Certifications';
import CompetencyMatrix from './pages/training/CompetencyMatrix';
import TrainingCourseDetail from './pages/training/TrainingCourseDetail';
import TrainingRecordDetail from './pages/training/TrainingRecordDetail';
import CertificationDetail from './pages/training/CertificationDetail';
import CompetencyMatrixDetail from './pages/training/CompetencyMatrixDetail';
// Phase 2 - Audits Module
import AuditsDashboard from './pages/audits/AuditsDashboard';
import AuditTemplates from './pages/audits/AuditTemplates';
import AuditRecords from './pages/audits/AuditRecords';
import ScheduleAudit from './pages/audits/ScheduleAudit';
import ConductAudit from './pages/audits/ConductAudit';
import AuditDetail from './pages/audits/AuditDetail';
// Phase 3 - PTW Module
import PTWList from './pages/ptw/PTWList';
import PTWCreate from './pages/ptw/PTWCreate';
import PTWDetail from './pages/ptw/PTWDetail';
// Phase 4 - Safety Meetings Module
import MeetingsList from './pages/meetings/MeetingsList';
import MeetingsCreate from './pages/meetings/MeetingsCreate';
import MeetingsEdit from './pages/meetings/MeetingsEdit';
import MeetingsDetail from './pages/meetings/MeetingsDetail';
// Phase 5 - SSEM Module
import SSEMDashboard from './pages/ssem/SSEMDashboard';
import SSEMAssessmentWizard from './pages/ssem/SSEMAssessmentWizard';
import SSEMRoadmap from './pages/ssem/SSEMRoadmap';
// Phase 6 - Document Control Module
import DocumentList from './pages/documents/DocumentList';
import DocumentCreate from './pages/documents/DocumentCreate';
import DocumentDetail from './pages/documents/DocumentDetail';
import DocumentEdit from './pages/documents/DocumentEdit';
// Notifications Page
import Notifications from './pages/Notifications';
// Platform Admin Pages
import PlatformAdminLogin from './pages/platform-admin/PlatformAdminLogin';
import PlatformAdminDashboard from './pages/platform-admin/PlatformAdminDashboard';
import PAAuditLogs from './pages/platform-admin/PAAuditLogs';
// RBAC Admin Pages
import RoleManagement from './pages/admin/RoleManagement';
import UserRoleAssignment from './pages/admin/UserRoleAssignment';
// New Hub Pages
import SafetyHub from './pages/SafetyHub';
import EnergyResourcesHub from './pages/EnergyResourcesHub';
import OnlineIntelligenceHub from './pages/OnlineIntelligenceHub';
import MRVHub from './pages/MRVHub';
import SustainabilityHub from './pages/SustainabilityHub';
import BoardPack from './pages/BoardPack';
// ERS Module Pages
import ERSDashboard from './pages/ers/ERSDashboard';
import CompanyMasterDataset from './pages/ers/CompanyMasterDataset';
import SiteMasterDataset from './pages/ers/SiteMasterDataset';
import ClientsList from './pages/ers/ClientsList';
import SitesList from './pages/ers/SitesList';
import StudiesList from './pages/ers/StudiesList';
import DesktopAssessment from './pages/ers/DesktopAssessment';
import SEUModule from './pages/ers/SEUModule';
import OnSiteModule from './pages/ers/OnSiteModule';
import ERSMain from './pages/ers/ERSMain';
import CorporateERSView from './pages/ers/CorporateERSView';
import ERSTrends from './pages/ers/ERSTrends';
import ERSMethodology from './pages/ers/ERSMethodology';
// Help Center
import HelpCenter from './pages/HelpCenter';
// Anonymous Reporting
import AnonymousReport from './pages/AnonymousReport';
import PublicReporting from './pages/admin/PublicReporting';
// Phase 0 — Multi-Site Foundation
import SetupWizard from './pages/admin/SetupWizard';
import OrgHierarchyPage from './pages/admin/OrgHierarchyPage';
import UserSiteRoles from './pages/admin/UserSiteRoles';
import SitePermissions from './pages/admin/SitePermissions';
import WorkforceAccess from './pages/admin/WorkforceAccess';
// Phase 2 — Multi-Site: Safety Module Site-Specific
import WorkforceInput from './pages/safety/WorkforceInput';
// IncidentIntelligence retired — content migrated into the Safety Dashboard;
// route now redirects. Page component kept in ./pages/safety/IncidentIntelligence.
// EHS MIS — site-scoped FY unit-performance grid (client workbook parity).
import EHSMISReport from './pages/safety/EHSMISReport';
// Comprehensive management MIS (17 sections) — distinct from the workbook grid.
import EHSManagementMIS from './pages/safety/EHSManagementMIS';
import ComplianceRegister from './pages/safety/ComplianceRegister';
import OccupationalHealth from './pages/safety/OccupationalHealth';
import ContractorManagement from './pages/contractors/ContractorManagement';
import ContractorDashboard from './pages/contractors/ContractorDashboard';
import ContractorDetail from './pages/contractors/ContractorDetail';
import WorkerDetail from './pages/contractors/WorkerDetail';
// CorporateSafetyDashboard retired — content migrated into the Safety Dashboard;
// route now redirects. Page kept in ./pages/dashboards/CorporateSafetyDashboard.
// SafetyOverviewDashboard retired — its panels are on the Safety Dashboard, which
// is now the single safety analytics surface; route now redirects. Page kept in
// ./pages/dashboards/SafetyOverviewDashboard (it and UnifiedDashboard still share
// SafetyDashboardView, which UnifiedDashboard uses for the Home dashboard).
import SafetyCommonDashboard from './pages/dashboards/SafetyCommonDashboard';
import UnifiedDashboard from './pages/dashboards/UnifiedDashboard';
import { useModuleAccess } from './hooks/useModuleAccess';
import CrossSiteRules from './pages/admin/CrossSiteRules';
import NotFound from './pages/NotFound';
// LCA top-level module (stakeholder decision: own menu item, sub-nav inside)
import LCAHub from './pages/lca/LCAHub';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor to add auth token and tenant ID
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');

  if (config.headers.Authorization) {
    // Sanitize explicitly-set Authorization headers (catches "Bearer null", "Bearer undefined")
    const authValue = config.headers.Authorization;
    if (authValue === 'Bearer null' || authValue === 'Bearer undefined' || authValue === 'Bearer ') {
      delete config.headers.Authorization;
      // Re-attach from localStorage if a valid token exists
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } else {
    // No header set — add from localStorage if available
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  // Add tenant ID header for multi-tenant isolation
  if (!config.headers['X-Tenant-ID']) {
    const tenantData = localStorage.getItem('tenant');
    if (tenantData) {
      try {
        const tenant = JSON.parse(tenantData);
        if (tenant && tenant.id) {
          config.headers['X-Tenant-ID'] = tenant.id;
        }
      } catch (err) {
        console.error('Failed to parse tenant data:', err);
      }
    }
  }

  return config;
});

// Axios response interceptor — clear stale tokens on 401
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only clear and redirect if we're not already on auth pages
      const path = window.location.pathname;
      const isAuthPage = path === '/login' || path === '/change-password' || path === '/accept-agreement' || path === '/forgot-password' || path === '/reset-password';
      if (!isAuthPage) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('tenant');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" />;
  return (
    <>
      {children}
      <Footer />
    </>
  );
};

// Resource-level RBAC gate backed by the cached PermissionContext — the same
// source the menus/tiles use — so the route stays consistent with tile
// visibility and never depends on a separate (failure-prone) permission API
// call. Renders nothing while permissions are still loading (so it never
// redirects too early), then fails closed. Admins bypass, matching the menu.
const RequireResourcePermission = ({ resource, action, redirectTo = '/', children }) => {
  const { getResourcePermissions, isAdmin, loading } = usePermissions();
  if (loading) return null;
  const allowed = (isAdmin && isAdmin()) ||
    getResourcePermissions(resource).some((p) => p.action === action);
  return allowed ? children : <Navigate to={redirectTo} replace />;
};

// Predicate variant of RequireResourcePermission, for surfaces whose rule is
// more than one resource grant. `allow(perms)` receives the usePermissions()
// value; the SafetyHub tile calls the same predicate (presets/safety.js). Same
// behaviour otherwise: nothing while permissions load, then fail closed.
const RequireAccess = ({ allow, redirectTo = '/', children }) => {
  const perms = usePermissions();
  if (perms.loading) return null;
  return allow(perms) ? children : <Navigate to={redirectTo} replace />;
};

// Module Protected Route Component - Combines auth + module access.
// Also renders the application Footer below the protected page so every
// authenticated route gets the same release-metadata footer (release version,
// env chip, support link) without each page having to import / place it.
const ModuleProtectedRoute = ({ children, moduleSlug, requireAny = null, requirePermission = null }) => {
  const token = localStorage.getItem('token');

  if (!token) {
    return <Navigate to="/login" />;
  }

  let content;
  if (requireAny) {
    const { ModuleGuardAny } = require('./hooks/useModuleAccess');
    content = (
      <ModuleGuardAny modules={requireAny} showUpgrade={true}>
        {children}
      </ModuleGuardAny>
    );
  } else {
    const { ModuleGuard } = require('./hooks/useModuleAccess');
    content = (
      <ModuleGuard module={moduleSlug} showUpgrade={true}>
        {children}
      </ModuleGuard>
    );
  }

  // Optional RBAC gate so direct navigation also fails closed: the module
  // being enabled for the tenant is not sufficient — the user must hold the
  // required permission. Redirects to the dashboard when denied.
  if (requirePermission) {
    content = (
      <RequireResourcePermission
        resource={requirePermission.resource}
        action={requirePermission.action}
        redirectTo="/safety"
      >
        {content}
      </RequireResourcePermission>
    );
  }

  return (
    <>
      {content}
      <Footer />
    </>
  );
};

// Landing decision. The new unified Home is the DEFAULT after login; a user
// who picked "Classic view" (localStorage saarv_home_pref='classic') gets the
// legacy dashboard. Guarded on the safety module so tenants without it never
// redirect into the module-gated Home (which would show an upgrade page).
const HomeRedirect = ({ user, onLogout }) => {
  const { isModuleEnabled, loading } = useModuleAccess();
  const { loading: permLoading } = usePermissions();
  const pref = localStorage.getItem('saarv_home_pref');
  if (pref === 'classic') {
    return <Dashboard user={user} onLogout={onLogout} />;
  }
  // Wait until BOTH module enablement AND permissions have settled. Module
  // access can resolve first while permissions are still loading, during which
  // isModuleEnabled('safety') fails closed (returns false) — rendering the
  // legacy Dashboard for a beat before the redirect. Gating on both avoids that
  // header/Quick-Actions flash.
  if (loading || permLoading) {
    return null;
  }
  // All safety users land on the framework Home; the widget set differs by role
  // (analytics vs personal My Work) — see safetyDashboardAccess.
  if (isModuleEnabled('safety')) {
    return <Navigate to="/dashboard/home" replace />;
  }
  return <Dashboard user={user} onLogout={onLogout} />;
};

function App() {
  const [user, setUser] = useState(null);
  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState(15); // Default 15 minutes

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Fetch company settings to get idle timeout configuration
  useEffect(() => {
    const fetchCompanySettings = async () => {
      if (!user) return;

      try {
        // Add timestamp to prevent caching
        const response = await axios.get(`${API}/company/settings?t=${Date.now()}`);
        const timeout = response.data.idle_timeout_minutes || 15;
        setIdleTimeoutMinutes(timeout);
      } catch (err) {
        console.error('[IdleTimeout] Failed to fetch company settings, using default 15 minutes', err);
      }
    };

    fetchCompanySettings();
  }, [user]);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = React.useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Remembered list filters must not leak to the next user of a shared
    // device (SiteContext clears its own keys on the same transition).
    clearAllListFilters();
    setUser(null);
    // Redirect to login so user sees the login page immediately
    window.location.href = '/login';
  }, []);

  // Idle timeout hook - only active when user is logged in
  const { showWarning, remainingSeconds, dismissWarning } = useIdleTimeout(
    idleTimeoutMinutes,
    1, // Show warning 1 minute before timeout
    handleLogout,
    !!user // enabled only when logged in
  );

  return (
    <AuthProvider>
      <PermissionProvider>
        <ModuleAccessProvider>
          <AlertProvider>
            <OrgProvider>
            <SiteProvider>
            <BrowserRouter>
            <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route path="/register" element={<CompanyRegister onLogin={handleLogin} />} />
        <Route path="/change-password" element={<ChangePassword onLogin={handleLogin} />} />
        <Route path="/accept-agreement" element={<AcceptAgreement onLogin={handleLogin} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/saarv-admin" element={<Navigate to="/saarv-admin/login" replace />} />
        <Route path="/saarv-admin/login" element={<PlatformAdminLogin />} />
        <Route path="/saarv-admin/dashboard" element={<PlatformAdminDashboard />} />
        <Route path="/saarv-admin/audit-logs" element={<PAAuditLogs />} />

        {/* Anonymous Incident Report - Public, no auth required */}
        <Route path="/report/:slug/:token" element={<AnonymousReport />} />

        {/* Help Center Route */}
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <HelpCenter />
            </ProtectedRoute>
          }
        />

        {/* New Hub Routes - Module Protected */}
        <Route
          path="/safety"
          element={
            <ModuleProtectedRoute moduleSlug="safety">
              <SafetyHub user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/energy-resources"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <ERSDashboard user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/sustainability"
          element={
            <ModuleProtectedRoute moduleSlug="sustainability">
              <SustainabilityHub user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/online-intelligence"
          element={
            <ModuleProtectedRoute moduleSlug="oim">
              <OnlineIntelligenceHub user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />

        <Route
          path="/mrv"
          element={
            <ModuleProtectedRoute moduleSlug="mrv">
              <MRVHub user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />

        <Route
          path="/board-pack"
          element={
            <ModuleProtectedRoute moduleSlug="board">
              <BoardPack user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />

        {/* ERS Module Routes */}
        <Route
          path="/ers"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <ERSDashboard user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ers/master-dataset"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <CompanyMasterDataset user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ers/site/:siteId/master-dataset"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <SiteMasterDataset user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ers/clients"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <ClientsList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ers/sites"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <SitesList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ers/studies"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <StudiesList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ers/study/:studyId"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <ERSMain user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />

        <Route
          path="/ers/desktop-assessment"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <DesktopAssessment user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />

        <Route
          path="/ers/seu-module/:studyId"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <SEUModule user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />

        <Route
          path="/ers/study/:studyId/onsite"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <OnSiteModule user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ers/corporate"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <CorporateERSView user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ers/study/:studyId/trends"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <ERSTrends user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ers/methodology"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <ERSMethodology user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        
        <Route
          path="/"
          element={
            <ProtectedRoute>
              {/* New Home is the default; "Classic view" (pref=classic) shows
                  the legacy dashboard. Guarded on the safety module. */}
              <HomeRedirect user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/home"
          element={
            // All safety users; the widget set is role-scoped inside the page
            // (management analytics vs personal My Work).
            <ModuleProtectedRoute moduleSlug="safety">
              <UnifiedDashboard user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/report"
          element={
            <ModuleProtectedRoute requireAny={['safety', 'incidents']}>
              <ReportHazard user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/hazards"
          element={
            <ModuleProtectedRoute requireAny={['safety', 'incidents']}>
              <HazardsList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/hazards/:id"
          element={
            <ModuleProtectedRoute requireAny={['safety', 'incidents']}>
              <HazardDetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/incidents"
          element={
            <ModuleProtectedRoute requireAny={['safety', 'incidents']}>
              <IncidentsList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/incidents/:id"
          element={
            <ModuleProtectedRoute requireAny={['safety', 'incidents']}>
              <IncidentDetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        {/* Observations module (UA – Unsafe Act / UC – Unsafe Condition) —
            split out of Incident Management. RBAC-gated per resource so
            direct navigation fails closed (SOC 2 CC6.1/CC6.3). */}
        <Route
          path="/observations"
          element={
            <ModuleProtectedRoute requireAny={['safety', 'observations']} requirePermission={{ resource: 'observations', action: 'read' }}>
              <ObservationsList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        {/* Declared before /observations/:id so 'create' isn't captured as an id. */}
        <Route
          path="/observations/create"
          element={
            <ModuleProtectedRoute requireAny={['safety', 'observations']} requirePermission={{ resource: 'observations', action: 'create' }}>
              <ReportObservation user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/observations/:id"
          element={
            <ModuleProtectedRoute requireAny={['safety', 'observations']} requirePermission={{ resource: 'observations', action: 'read' }}>
              <ObservationDetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <UserManagement user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminManagement user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lca"
          element={
            <ProtectedRoute>
              <LCAHub user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/roles"
          element={
            <ProtectedRoute>
              <RoleManagement user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users/roles"
          element={
            <ProtectedRoute>
              <UserRoleAssignment user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/public-reporting"
          element={
            <ProtectedRoute>
              <PublicReporting user={user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        {/* Phase 1 Routes */}
        <Route
          path="/dashboard/executive"
          element={
            // Same `analytics:read` grant as the other analytics surfaces, in
            // place of a second, differently-spelled role list.
            <ModuleProtectedRoute moduleSlug="safety">
              <RequireResourcePermission resource="analytics" action="read">
                <ExecutiveDashboard user={user} onLogout={handleLogout} />
              </RequireResourcePermission>
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/dashboard/supervisor"
          element={
            // Admin OR analytics:read OR a supervisor/frontline role — the same
            // predicate as the SafetyHub tile (presets/safety.js).
            <ModuleProtectedRoute moduleSlug="safety">
              <RequireAccess allow={(perms) => canAccessSupervisorDashboard(perms, user?.role)}>
                <SupervisorDashboard user={user} onLogout={handleLogout} />
              </RequireAccess>
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/capa"
          element={
            <ModuleProtectedRoute moduleSlug="capa">
              <CAPAList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/capa/create"
          element={
            <ModuleProtectedRoute moduleSlug="capa">
              <CAPACreate user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/capa/:id"
          element={
            <ModuleProtectedRoute moduleSlug="capa">
              <CAPADetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        {/* Phase 2 - HIRA Routes */}
        <Route
          path="/hira"
          element={
            <ModuleProtectedRoute moduleSlug="hira" requirePermission={{ resource: 'hira', action: 'read', moduleSlug: 'hira' }}>
              <HIRAList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/hira/create"
          element={
            <ModuleProtectedRoute moduleSlug="hira" requirePermission={{ resource: 'hira', action: 'read', moduleSlug: 'hira' }}>
              <HIRACreate user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/hira/:id"
          element={
            <ModuleProtectedRoute moduleSlug="hira" requirePermission={{ resource: 'hira', action: 'read', moduleSlug: 'hira' }}>
              <HIRADetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        {/* Phase 2 - Training Routes */}
        <Route
          path="/training"
          element={
            <ModuleProtectedRoute moduleSlug="training">
              <TrainingDashboard user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/training/courses"
          element={
            <ModuleProtectedRoute moduleSlug="training">
              <TrainingCourses user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/training/records"
          element={
            <ModuleProtectedRoute moduleSlug="training">
              <TrainingRecords user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/training/certifications"
          element={
            <ModuleProtectedRoute moduleSlug="training">
              <Certifications user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/training/competency"
          element={
            <ModuleProtectedRoute moduleSlug="training">
              <CompetencyMatrix user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/training/courses/:id"
          element={
            <ModuleProtectedRoute moduleSlug="training">
              <TrainingCourseDetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/training/records/:id"
          element={
            <ModuleProtectedRoute moduleSlug="training">
              <TrainingRecordDetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/training/certifications/:id"
          element={
            <ModuleProtectedRoute moduleSlug="training">
              <CertificationDetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/training/competency/:id"
          element={
            <ModuleProtectedRoute moduleSlug="training">
              <CompetencyMatrixDetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/audits"
          element={
            <ModuleProtectedRoute moduleSlug="audits">
              <AuditsDashboard user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/audits/templates"
          element={
            <ModuleProtectedRoute moduleSlug="audits">
              <AuditTemplates user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/audits/records"
          element={
            <ModuleProtectedRoute moduleSlug="audits">
              <AuditRecords user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/audits/schedule"
          element={
            <ModuleProtectedRoute moduleSlug="audits">
              <ScheduleAudit user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/audits/conduct"
          element={
            <ModuleProtectedRoute moduleSlug="audits">
              <ConductAudit user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/audits/detail/:auditId"
          element={
            <ModuleProtectedRoute moduleSlug="audits">
              <AuditDetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        {/* Phase 3 - PTW Routes */}
        <Route
          path="/ptw"
          element={
            <ModuleProtectedRoute moduleSlug="ptw">
              <PTWList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ptw/create"
          element={
            <ModuleProtectedRoute moduleSlug="ptw">
              <PTWCreate user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ptw/edit/:id"
          element={
            <ModuleProtectedRoute moduleSlug="ptw">
              <PTWCreate user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ptw/:id"
          element={
            <ModuleProtectedRoute moduleSlug="ptw">
              <PTWDetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        {/* Phase 4 - Safety Meetings Routes */}
        <Route
          path="/meetings"
          element={
            <ModuleProtectedRoute moduleSlug="meetings">
              <MeetingsList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/meetings/create"
          element={
            <ModuleProtectedRoute moduleSlug="meetings">
              <MeetingsCreate user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/meetings/:id/edit"
          element={
            <ModuleProtectedRoute moduleSlug="meetings">
              <MeetingsEdit user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/meetings/:id"
          element={
            <ModuleProtectedRoute moduleSlug="meetings">
              <MeetingsDetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        {/* Phase 5 - SSEM Routes. ssem:read mirrors the backend router guard
            and the Safety Hub tile, so the tile and the route always agree. */}
        <Route
          path="/ssem"
          element={
            <ModuleProtectedRoute moduleSlug="ssem">
              <RequireResourcePermission resource="ssem" action="read">
                <SSEMDashboard user={user} onLogout={handleLogout} />
              </RequireResourcePermission>
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ssem/wizard"
          element={
            <ModuleProtectedRoute moduleSlug="ssem">
              <RequireResourcePermission resource="ssem" action="read">
                <SSEMAssessmentWizard user={user} onLogout={handleLogout} />
              </RequireResourcePermission>
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ssem/roadmap/:assessmentId"
          element={
            <ModuleProtectedRoute moduleSlug="ssem">
              <RequireResourcePermission resource="ssem" action="read">
                <SSEMRoadmap user={user} onLogout={handleLogout} />
              </RequireResourcePermission>
            </ModuleProtectedRoute>
          }
        />
        {/* Phase 6 - Document Control Routes */}
        <Route
          path="/documents"
          element={
            <ModuleProtectedRoute moduleSlug="documents">
              <DocumentList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/documents/create"
          element={
            <ModuleProtectedRoute moduleSlug="documents">
              <DocumentCreate user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/documents/:id/edit"
          element={
            <ModuleProtectedRoute moduleSlug="documents">
              <DocumentEdit user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/documents/:id"
          element={
            <ModuleProtectedRoute moduleSlug="documents">
              <DocumentDetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        {/* Phase 0 — Multi-Site: Org Hierarchy & Setup Wizard */}
        <Route
          path="/admin/org-hierarchy"
          element={
            <ProtectedRoute>
              <OrgHierarchyPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/setup-wizard"
          element={
            <ProtectedRoute>
              <SetupWizard user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        {/* Phase 1 — Multi-Site: RBAC Site Roles & Permissions */}
        <Route
          path="/admin/site-roles"
          element={
            <ProtectedRoute>
              <UserSiteRoles user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/site-permissions"
          element={
            <ProtectedRoute>
              <SitePermissions user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/workforce-access"
          element={
            <ProtectedRoute>
              <WorkforceAccess user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        {/* Phase 2 — Multi-Site: Safety Module Routes */}
        <Route
          path="/safety/workforce"
          element={
            // Role-gated like the backend (_require_workforce_role) so a
            // non-allowed role is sent back to the hub instead of a 403 page.
            <ModuleProtectedRoute moduleSlug="safety">
              <RequireAccess redirectTo="/safety" allow={() => canAccessWorkforce(user?.role)}>
                <WorkforceInput user={user} onLogout={handleLogout} />
              </RequireAccess>
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/safety/intelligence"
          element={
            // Retired: Incident Intelligence content was migrated into the
            // Safety Dashboard (via Safety Overview, itself now retired). Points
            // at the surviving page directly rather than through two redirects.
            // The legacy page component is kept but no longer routed.
            <Navigate to="/safety/dashboard" replace />
          }
        />
        {/* Legal References merged into Legal Compliance as its References
            tab. Kept as a redirect rather than deleted: the old path is in
            bookmarks and in links already sent to customers, and a 404 on a
            compliance screen reads as lost data. */}
        <Route
          path="/safety/legal-references"
          element={<Navigate to="/safety/compliance-register?tab=references" replace />}
        />
        {/* EHS MIS unit-performance report. The backend gates on the safety
            read grant and resolves the site fail-closed, so the route only
            needs the module gate. */}
        <Route
          path="/safety/ehs-mis"
          element={
            <ModuleProtectedRoute moduleSlug="safety">
              <EHSMISReport user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        {/* Comprehensive EHS management MIS. Server-side per-metric RBAC means a
            section the user may not see comes back marked restricted. */}
        <Route
          path="/safety/ehs-management-mis"
          element={
            <ModuleProtectedRoute moduleSlug="safety">
              <EHSManagementMIS user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        {/* Legal compliance register — the data entry behind the MIS compliance
            section. Reads need the safety read grant and writes the
            reports:create grant, both enforced by /api/compliance itself. */}
        <Route
          path="/safety/compliance-register"
          element={
            <ModuleProtectedRoute moduleSlug="safety">
              <ComplianceRegister user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        {/* Occupational health. The route is open to the safety module because
            the page's default tab is aggregates only; the clinical tabs appear
            only for a caller the server reports as holding the dedicated
            occupational_health grant, and the endpoints enforce it regardless. */}
        <Route
          path="/safety/occupational-health"
          element={
            <ModuleProtectedRoute moduleSlug="safety">
              <OccupationalHealth user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/contractors"
          element={
            <ModuleProtectedRoute moduleSlug="safety">
              <RequireResourcePermission resource="contractors" action="read">
                <ContractorManagement user={user} onLogout={handleLogout} />
              </RequireResourcePermission>
            </ModuleProtectedRoute>
          }
        />
        {/* Contractor dashboard, contractor detail and worker detail.
            Same UI gate as the list above — and a UI gate only: every
            contractor endpoint independently checks contractors:read and
            resolves site scope server-side, so these pages cannot show a
            reader anything their token does not already permit. */}
        <Route
          path="/contractors/dashboard"
          element={
            <ModuleProtectedRoute moduleSlug="safety">
              <RequireResourcePermission resource="contractors" action="read">
                <ContractorDashboard user={user} onLogout={handleLogout} />
              </RequireResourcePermission>
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/contractors/workers/:workerId"
          element={
            <ModuleProtectedRoute moduleSlug="safety">
              <RequireResourcePermission resource="contractors" action="read">
                <WorkerDetail user={user} onLogout={handleLogout} />
              </RequireResourcePermission>
            </ModuleProtectedRoute>
          }
        />
        {/* Declared AFTER the two literal paths above: a parameterised route
            registered first would swallow "dashboard" as a contractor id. */}
        <Route
          path="/contractors/:contractorId"
          element={
            <ModuleProtectedRoute moduleSlug="safety">
              <RequireResourcePermission resource="contractors" action="read">
                <ContractorDetail user={user} onLogout={handleLogout} />
              </RequireResourcePermission>
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/dashboard/corporate-safety"
          element={
            // Retired: Corporate Safety Dashboard content was migrated into the
            // Safety Dashboard (via Safety Overview, itself now retired). Its
            // cross-site panels — site comparison, workforce, high-risk sites —
            // are on that page. The legacy component is kept but no longer routed.
            <Navigate to="/safety/dashboard" replace />
          }
        />
        <Route
          path="/dashboard/safety-overview"
          element={
            // Retired: Safety Overview's panels — the full KPI grid, workforce,
            // high-risk sites, year-on-year, site comparison and the type pivots
            // — are all on the Safety Dashboard, which is now the single safety
            // analytics surface. Redirect rather than delete: this path is in
            // bookmarks and in links already sent to customers.
            //
            // NOTE: per-user saved layouts are keyed per dashboard, so a layout
            // customised on Overview does not follow the reader here; they land
            // on the Safety Dashboard default until they customise it. Deliberate
            // — the two pages' widget sets differ, and copying one arrangement
            // onto the other would produce an arrangement nobody chose.
            <Navigate to="/safety/dashboard" replace />
          }
        />
        <Route
          path="/safety/dashboard"
          element={
            // Common Safety Dashboard: the KPI → trend → breakdown →
            // drill-down → record → action surface. Gated on `analytics:read`,
            // the grant the tenant's roles already carry, rather than a
            // hard-coded role list — which locked out Manager and Safety
            // Officer despite them holding it, and every custom role however
            // it was configured. Fails closed on direct navigation; every
            // metric and drill-down is independently RBAC-checked and
            // site-scoped server-side.
            <ModuleProtectedRoute moduleSlug="safety">
              <RequireResourcePermission resource="analytics" action="read">
                <SafetyCommonDashboard user={user} onLogout={handleLogout} />
              </RequireResourcePermission>
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/admin/cross-site-rules"
          element={
            <ProtectedRoute>
              <CrossSiteRules user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        {/* Catch-all — MUST stay last. Without it an unmatched URL rendered
            nothing at all: a white page with no header and no way back, which
            is how a wrong notification link (overdue training pointed at
            /training/{id} instead of /training/records/{id}) reached users
            looking like an outage. Wrapped in ProtectedRoute so an unmatched
            URL from a logged-out visitor still goes to /login rather than
            advertising which routes exist. */}
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <NotFound user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        </Routes>
        {/* Safety Intelligent Agent — visible only when the safety_assistant
            module is enabled for the tenant (platform-admin toggle) */}
        <ModuleGuard module="safety_assistant">
          <AssistantWidget />
        </ModuleGuard>
      </BrowserRouter>
      </SiteProvider>
      </OrgProvider>

      {/* Idle Timeout Warning Modal */}
      <IdleTimeoutWarning
        show={showWarning}
        remainingSeconds={remainingSeconds}
        onContinue={dismissWarning}
      />

      <Toaster />
      {/* Sonner toaster: the LCA module + ProductMaster call toast() from
          'sonner', which renders nothing unless its Toaster is mounted. Without
          this, validation/success messages there fail silently. */}
      <SonnerToaster richColors closeButton />
    </AlertProvider>
    </ModuleAccessProvider>
      </PermissionProvider>
    </AuthProvider>
  );
}

export default App;
export { API };